/**
 * What PLC code looks like, line by line.
 *
 * The predicate this replaces asked whether the text *mentioned* `VAR`,
 * `PROGRAM`, `:=` or `BOOL`. Prose about a PLC program mentions all of them:
 * on 2026-09-20 an engineering-reasoning paragraph followed by a model's
 * `[CLARIFY]` question was counted as a 25-line program, and everything
 * downstream — the step's conclusion, the compile step, the verdict hold —
 * believed it.
 *
 * Two grades, because they answer different questions. *Evidence* is a line
 * only ST has: a POU header, a bare VAR block opener, an assignment or a
 * named-argument call using `:=`. *Code-like* also admits lines other languages
 * share (`foo(x);`, `x: number;`, control keywords, comments), which are only
 * allowed to count toward a majority, never to prove anything on their own.
 *
 * Shared by both tiers: the browser decides with it what to show as the
 * program, the runtime whether a reply is one.
 */
const IDENT = String.raw `(?:"[^"\n]+"|[\p{L}_][\p{L}\p{N}_]*)`;
// `^` dereferences a pointer or names the instance: `THIS^.x`, `pBuf^[i]`.
const PATH = String.raw `#?${IDENT}\^?(?:\s*\.\s*${IDENT}\^?|\s*\[[^\]\n]*\])*`;
const TRAILING_COMMENT = String.raw `\s*(?://.*|\(\*.*\*\))?\s*$`;
// A block's name is ASCII or quoted. Unicode letters are fine in a variable,
// but after an uppercase keyword they are what a Chinese sentence looks like:
// 「FUNCTION 块用于封装启停逻辑」 would otherwise be a function named 块用于….
const POU_NAME = String.raw `(?:"[^"\n]+"|[A-Za-z_][A-Za-z0-9_]*)`;
const MODIFIERS = String.raw `(?:(?:PUBLIC|PRIVATE|PROTECTED|INTERNAL|FINAL|ABSTRACT)\s+)*`;
const INHERITANCE = String.raw `(?:\s+EXTENDS\s+${POU_NAME})?(?:\s+IMPLEMENTS\s+${POU_NAME}(?:\s*,\s*${POU_NAME})*)?`;
const RETURN_TYPE = String.raw `(?:\s*:\s*(?:(?:ARRAY\s*\[[^\]\n]*\]\s*OF|REF_TO|POINTER\s+TO)\s+)?(?:"[^"\n]+"|[A-Za-z_][A-Za-z0-9_]*(?:\s*[(\[][^)\]\n]*[)\]])?))?`;
// A header is the keyword, a name, and nothing a sentence would add after it.
// Anchored only at its start, 「PROGRAM Main 负责调用各功能块」 and
// "Program structure: …" were each proof of a program.
//
// IEC keywords are case-insensitive, and engineers paste lowercase ST.
// A ladder block names its language after the block name: `… LANGUAGE LAD`.
const POU_HEAD_ANY_CASE = new RegExp(String.raw `^\s*(?:PROGRAM|FUNCTION_BLOCK|ORGANIZATION_BLOCK|DATA_BLOCK)\s+${MODIFIERS}${POU_NAME}${INHERITANCE}(?:\s+LANGUAGE\s+LAD)?${TRAILING_COMMENT}`, 'iu');
// Uppercase only: the lowercase forms are JS/TS declarations. These are the
// ones that may carry a type, and a TYPE or ACTION may end in a colon.
const POU_HEAD_UPPER_ONLY = new RegExp(String.raw `^\s*(?:FUNCTION|TYPE|INTERFACE|METHOD|ACTION|PROPERTY)\s+${MODIFIERS}${POU_NAME}${RETURN_TYPE}${INHERITANCE}\s*:?${TRAILING_COMMENT}`, 'u');
const VAR_BLOCK_OPEN = new RegExp(String.raw `^\s*VAR(?:_INPUT|_OUTPUT|_IN_OUT|_GLOBAL|_TEMP|_STAT|_EXTERNAL|_CONFIG)?(?:\s+(?:RETAIN|NON_RETAIN|PERSISTENT|CONSTANT))*${TRAILING_COMMENT}`, 'i');
const ASSIGNMENT = new RegExp(String.raw `^\s*${PATH}\s*:=\s*[^;\n]+;${TRAILING_COMMENT}`, 'u');
const NAMED_ARG_CALL = new RegExp(String.raw `^\s*${PATH}\s*\([^\n]*:=[^\n]*\)\s*;${TRAILING_COMMENT}`, 'u');
const PLAIN_CALL = new RegExp(String.raw `^\s*${PATH}\s*\([^\n]*\)\s*;${TRAILING_COMMENT}`, 'u');
const DECLARATION = new RegExp(String.raw `^\s*${IDENT}(?:\s*,\s*${IDENT})*\s*(?:AT\s+%[A-Za-z0-9.*]+\s*)?:\s*[^;\n]+;${TRAILING_COMMENT}`, 'u');
const BLOCK_END = new RegExp(String.raw `^\s*END_[A-Za-z_]+\s*;?${TRAILING_COMMENT}`, 'i');
const CONTROL_OPEN = new RegExp(String.raw `^\s*(?:IF|ELSIF|CASE|FOR|WHILE)\b.*\b(?:THEN|OF|DO)${TRAILING_COMMENT}`, 'i');
// `IF x THEN y := 1; END_IF;` on one line, and the tail of a REPEAT.
const CONTROL_ONE_LINE = new RegExp(String.raw `^\s*(?:IF|ELSIF|CASE|FOR|WHILE)\b.*;${TRAILING_COMMENT}`, 'i');
const UNTIL = new RegExp(String.raw `^\s*UNTIL\b[^。，：；]+?;?${TRAILING_COMMENT}`);
const CONTROL_BARE = new RegExp(String.raw `^\s*(?:ELSE|REPEAT|BEGIN|RETURN|EXIT|CONTINUE|STRUCT|UNION|NETWORK)\s*;?${TRAILING_COMMENT}`, 'i');
const REGION = /^\s*REGION\b.*$/;
const LABEL_VALUE = String.raw `(?:-?\d+(?:\s*\.\.\s*-?\d+)?|[A-Za-z_][A-Za-z0-9_.#]*)`;
// `0:`, `10, 20:`, `1..5:`, `E_State.IDLE:` — alone, or with the statement after it.
const CASE_LABEL = new RegExp(String.raw `^\s*${LABEL_VALUE}(?:\s*,\s*${LABEL_VALUE})*\s*:(?!=)(?:${TRAILING_COMMENT}|\s*\S[^\n]*;${TRAILING_COMMENT})`);
const COMMENT_OR_ATTRIBUTE = /^\s*(?:\/\/.*|\(\*.*\*\)|\{[^}\n]*\})\s*$/;
// Ladder text: a network header with its title, and a rung — a condition, then
// `=>` into a coil or an instruction box. A sentence with an arrow in it has
// full-width punctuation before the arrow, or no coil or box after it.
const LAD_NETWORK = new RegExp(String.raw `^\s*NETWORK(?:\s+"[^"\n]*")?${TRAILING_COMMENT}`, 'i');
const LAD_RUNG = /^[^。，：；？！、\n]*=>\s*(?:\(\s*(?:[SR]\s+)?[#"\p{L}_]|(?:TON|TOF|TP|CTU|CTD|CTUD|MOVE|CALL|P_TRIG|N_TRIG)\b)/u;
function isEvidenceLine(line) {
    return POU_HEAD_ANY_CASE.test(line)
        || POU_HEAD_UPPER_ONLY.test(line)
        || VAR_BLOCK_OPEN.test(line)
        || ASSIGNMENT.test(line)
        || NAMED_ARG_CALL.test(line);
}
function isCodeLikeLine(line) {
    return isEvidenceLine(line)
        || PLAIN_CALL.test(line)
        || DECLARATION.test(line)
        || BLOCK_END.test(line)
        || CONTROL_OPEN.test(line)
        || CONTROL_ONE_LINE.test(line)
        || UNTIL.test(line)
        || CONTROL_BARE.test(line)
        || REGION.test(line)
        || CASE_LABEL.test(line)
        || COMMENT_OR_ATTRIBUTE.test(line)
        || LAD_NETWORK.test(line)
        || LAD_RUNG.test(line);
}
// ── Statements written across lines ─────────────────────────────────────────
//
// A function block call with one argument per line, a boolean expression broken
// before each AND, a `(* … *)` header: none of their lines is a statement on its
// own, and a program made of them was judged to be mostly not code.
/** How far a statement may run before it is assumed never to have been one. */
const MAX_CONTINUATION_LINES = 12;
/** An assignment that has not reached its `;`. */
const OPEN_ASSIGNMENT = new RegExp(String.raw `^\s*${PATH}\s*:=[^;\n]*$`, 'u');
/** A call whose parenthesis is still open and whose line ends mid-list. */
const OPEN_CALL = new RegExp(String.raw `^\s*${PATH}\s*\([^)\n]*[,(]\s*$`, 'u');
/** A condition still being written. Uppercase: "If the guard …" opens a sentence. */
const OPEN_CONTROL = /^\s*(?:IF|ELSIF|WHILE|UNTIL|FOR|CASE)\b/;
/**
 * What a line of an explanation has and a line of a statement does not:
 * full-width punctuation, or the way a markdown line begins. Judged with the
 * trailing comment removed — comments are where the Chinese in a program lives.
 */
const PROSE_MARK = /[。，：；？！、]|^\s*(?:[-*+]\s|#{1,6}\s|\d+[.)]\s|\*\*|>)/;
function withoutTrailingComment(line) {
    return line.replace(/\s*(?:\/\/.*|\(\*.*\*\))\s*$/, '');
}
function opensStatement(line) {
    const code = withoutTrailingComment(line);
    if (PROSE_MARK.test(code))
        return false;
    return OPEN_ASSIGNMENT.test(code) || OPEN_CALL.test(code) || OPEN_CONTROL.test(code);
}
function closesStatement(line) {
    return /(?:;|\b(?:THEN|DO|OF))\s*$/i.test(withoutTrailingComment(line));
}
/** Which of these lines read as code, given the lines before them. */
function codeLikeLines(lines) {
    const flags = [];
    let inBlockComment = false;
    let continuationLeft = 0;
    for (const line of lines) {
        if (inBlockComment) {
            flags.push(true);
            if (line.includes('*)'))
                inBlockComment = false;
            continue;
        }
        if (/^\s*\(\*/.test(line) && !line.includes('*)')) {
            inBlockComment = true;
            flags.push(true);
            continue;
        }
        if (continuationLeft > 0) {
            // A sentence ends the statement it interrupted: it was not one.
            if (PROSE_MARK.test(withoutTrailingComment(line))) {
                continuationLeft = 0;
            }
            else {
                flags.push(true);
                continuationLeft = closesStatement(line) ? 0 : continuationLeft - 1;
                continue;
            }
        }
        if (isCodeLikeLine(line)) {
            flags.push(true);
        }
        else if (opensStatement(line)) {
            flags.push(true);
            continuationLeft = MAX_CONTINUATION_LINES;
        }
        else {
            flags.push(false);
        }
    }
    return flags;
}
function linesOf(text) {
    return typeof text === 'string' ? text.split(/\r?\n/) : [];
}
/** Whether the text contains at least one line only PLC code would contain. */
export function looksLikePlcCode(code) {
    return linesOf(code).some(isEvidenceLine);
}
/**
 * Whether the text is a program rather than a text that contains some.
 *
 * For the callers that are about to treat a whole, unfenced text as code.
 * A fence is the author's own statement that what is inside is code; without
 * one, a single illustrative statement inside an explanation must not turn the
 * explanation into the program.
 */
export function isMostlyPlcCode(text, threshold = 0.6) {
    const lines = linesOf(text).filter(line => line.trim().length > 0);
    if (lines.length === 0 || !lines.some(isEvidenceLine))
        return false;
    return codeLikeLines(lines).filter(Boolean).length / lines.length >= threshold;
}
/**
 * The lines of a text that read as code, in order.
 *
 * For the callers that have to say how much code an unfenced text holds, or
 * what shape it has. A sentence that mentions `:=` is not a line of code, and
 * an explanation sixteen lines long with one such sentence in it was counted
 * as sixteen lines of code.
 *
 * None where no line is one only PLC code would contain: the code-like lines
 * prove nothing on their own.
 */
export function plcCodeLines(text) {
    const lines = linesOf(text).filter(line => line.trim().length > 0);
    if (!lines.some(isEvidenceLine))
        return [];
    const flags = codeLikeLines(lines);
    return lines.filter((_, index) => flags[index]);
}
//# sourceMappingURL=plcCodeShape.js.map